import { useEffect, useState } from "react";
import { api, formatRupiah, currentYM, dispDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, FileDown, Save } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

export default function AdminPayslip() {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [period, setPeriod] = useState(currentYM());
  const [items, setItems] = useState([]);
  const [slip, setSlip] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => {
      const monthly = r.data.filter((u) => u.role === "pegawai" && u.pay_type !== "hourly");
      setUsers(monthly);
      if (monthly.length && !userId) setUserId(monthly[0].id);
    });
  }, []);

  const loadData = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [ded, sl] = await Promise.all([
        api.get("/payslip/deductions", { params: { user_id: userId, period } }),
        api.get("/payslip/admin", { params: { user_id: userId, period } }),
      ]);
      // assign stable client-side _cid for React keys
      setItems((ded.data.items || []).map((it) => ({ ...it, _cid: crypto.randomUUID() })));
      setSlip(sl.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [userId, period]);

  const addItem = () => setItems([...items, { _cid: crypto.randomUUID(), label: "", amount: 0, note: "" }]);
  const removeItem = (cid) => setItems(items.filter((it) => it._cid !== cid));
  const updateItem = (cid, key, val) => setItems(items.map((it) => it._cid === cid ? { ...it, [key]: val } : it));

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/payslip/deductions", {
        user_id: userId,
        period,
        items: items.map(({ _cid, ...rest }) => ({ ...rest, amount: parseFloat(rest.amount) || 0 })),
      });
      toast.success("Potongan disimpan");
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const r = await api.get("/payslip/pdf", { params: { user_id: userId, period }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `slip-gaji-${slip?.user?.employee_id}-${period}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal unduh PDF"); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
      <p className="overline">Penggajian</p>
      <h1 className="font-display text-3xl font-bold tracking-tight">Slip Gaji</h1>
      <p className="text-sm text-zinc-500 mt-1">Kelola potongan dan generate slip gaji bulanan untuk pegawai bulanan.</p>

      <Card className="mt-6 p-4 border border-zinc-200 shadow-none">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label>Pegawai</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="mt-1" data-testid="ps-user"><SelectValue placeholder="Pilih pegawai" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.employee_id})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Periode (Bulan)</Label>
            <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="mt-1" data-testid="ps-period" />
          </div>
          <Button onClick={downloadPDF} disabled={!slip} className="bg-primary hover:bg-primary/90" data-testid="btn-download-pdf">
            <FileDown className="w-4 h-4 mr-1.5" /> Download PDF
          </Button>
        </div>
      </Card>

      {loading && <PageLoader label="Memuat slip..." />}

      {!loading && slip && (
        <Tabs defaultValue="config" className="mt-6">
          <TabsList>
            <TabsTrigger value="config" data-testid="ps-tab-config">Konfigurasi Potongan</TabsTrigger>
            <TabsTrigger value="preview" data-testid="ps-tab-preview">Preview Slip</TabsTrigger>
          </TabsList>

          <TabsContent value="config" className="mt-4 space-y-4">
            <Card className="p-6 border border-zinc-200 shadow-none">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="overline">Potongan Manual</p>
                  <h3 className="font-display text-lg font-semibold">Item Potongan</h3>
                  <p className="text-xs text-zinc-500 mt-1">Contoh: BPJS, Pinjaman pribadi ke owner, dll. Potongan kasbon otomatis diambil dari approved bulan ini.</p>
                </div>
                <Button onClick={addItem} variant="outline" size="sm" data-testid="btn-add-deduction">
                  <Plus className="w-4 h-4 mr-1" /> Tambah
                </Button>
              </div>

              {items.length === 0 && <p className="text-sm text-zinc-500">Belum ada potongan manual.</p>}

              {items.map((it, i) => (
                <div key={it._cid} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`deduction-row-${i}`}>
                  <div className="col-span-4">
                    <Label className="text-xs">Kategori</Label>
                    <Input value={it.label} onChange={(e) => updateItem(it._cid, "label", e.target.value)} placeholder="BPJS / Pinjaman / dll" className="mt-1" data-testid={`ded-label-${i}`} />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Nominal</Label>
                    <Input type="number" value={it.amount} onChange={(e) => updateItem(it._cid, "amount", e.target.value)} className="mt-1" data-testid={`ded-amount-${i}`} />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">Catatan</Label>
                    <Input value={it.note} onChange={(e) => updateItem(it._cid, "note", e.target.value)} className="mt-1" data-testid={`ded-note-${i}`} />
                  </div>
                  <div className="col-span-1">
                    <Button variant="ghost" size="icon" onClick={() => removeItem(it._cid)} data-testid={`ded-remove-${i}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}

              <Button onClick={save} disabled={busy} className="mt-4 bg-primary hover:bg-primary/90" data-testid="btn-save-deductions">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Simpan Potongan</>}
              </Button>
            </Card>
          </TabsContent>

          <TabsContent value="preview" className="mt-4">
            <SlipPreview slip={slip} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

export const SlipPreview = ({ slip }) => (
  <Card className="p-6 border border-zinc-200 shadow-none">
    <div className="flex items-center justify-between mb-6 pb-4 border-b border-zinc-200">
      <div>
        <p className="overline text-primary">RelayLab Autolight</p>
        <h2 className="font-display text-2xl font-bold tracking-tight">Slip Gaji</h2>
        <p className="text-sm text-zinc-600">{slip.user.name} ({slip.user.employee_id}) · Periode: <b>{slip.period_label}</b></p>
      </div>
    </div>

    <p className="overline mb-2">A. Perhitungan Gaji Bulanan</p>
    <table className="w-full text-sm border-collapse mb-2">
      <thead>
        <tr className="bg-zinc-50">
          <th className="text-left px-3 py-2 font-semibold">Komponen</th>
          <th className="text-left px-3 py-2 font-semibold">Catatan</th>
          <th className="text-right px-3 py-2 font-semibold">Jumlah</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-zinc-100">
        <tr><td className="px-3 py-1.5">Gaji Pokok</td><td></td><td className="text-right">{formatRupiah(slip.gaji_pokok)}</td></tr>
        {slip.kasbon_items.length > 0 && (
          <tr><td colSpan={3} className="px-3 pt-2 pb-1 font-semibold text-zinc-700">Potongan Kasbon</td></tr>
        )}
        {slip.kasbon_items.map((k, i) => (
          <tr key={`k-${i}`}>
            <td className="px-3 py-1 pl-6 text-zinc-700">Kasbon {dispDate(k.date)}</td>
            <td className="px-3 py-1 text-xs text-zinc-500">{k.note}</td>
            <td className="px-3 py-1 text-right text-red-600">({formatRupiah(k.amount)})</td>
          </tr>
        ))}
        {slip.kasbon_items.length > 0 && (
          <tr className="italic">
            <td className="px-3 py-1 pl-6 text-xs">Subtotal Kasbon</td>
            <td></td>
            <td className="px-3 py-1 text-right text-red-600">({formatRupiah(slip.total_kasbon)})</td>
          </tr>
        )}
        {slip.manual_items.length > 0 && (
          <tr><td colSpan={3} className="px-3 pt-2 pb-1 font-semibold text-zinc-700">Potongan Manual</td></tr>
        )}
        {slip.manual_items.map((m, i) => (
          <tr key={`m-${i}`}>
            <td className="px-3 py-1 pl-6 text-zinc-700">{m.label}</td>
            <td className="px-3 py-1 text-xs text-zinc-500">{m.note}</td>
            <td className="px-3 py-1 text-right text-red-600">({formatRupiah(m.amount)})</td>
          </tr>
        ))}
        {slip.manual_items.length > 0 && (
          <tr className="italic">
            <td className="px-3 py-1 pl-6 text-xs">Subtotal Manual</td>
            <td></td>
            <td className="px-3 py-1 text-right text-red-600">({formatRupiah(slip.total_manual)})</td>
          </tr>
        )}
        {slip.loan_items && slip.loan_items.length > 0 && (
          <tr><td colSpan={3} className="px-3 pt-2 pb-1 font-semibold text-zinc-700">Cicilan Pinjaman</td></tr>
        )}
        {(slip.loan_items || []).map((l, i) => (
          <tr key={`l-${i}`}>
            <td className="px-3 py-1 pl-6 text-zinc-700">
              {l.title}
              {l.is_override && (
                <span className="ml-1.5 inline-flex items-center text-[0.65rem] px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700 border border-blue-300">extra</span>
              )}
            </td>
            <td className="px-3 py-1 text-xs text-zinc-500">
              {l.category} · Sisa: {formatRupiah(l.outstanding_after)}
              {l.note ? ` · ${l.note}` : ""}
            </td>
            <td className="px-3 py-1 text-right text-red-600">({formatRupiah(l.amount)})</td>
          </tr>
        ))}
        {slip.loan_items && slip.loan_items.length > 0 && (
          <tr className="italic">
            <td className="px-3 py-1 pl-6 text-xs">Subtotal Cicilan Pinjaman</td>
            <td></td>
            <td className="px-3 py-1 text-right text-red-600">({formatRupiah(slip.total_loan || 0)})</td>
          </tr>
        )}
        <tr className="border-t border-zinc-300">
          <td className="px-3 py-2 font-semibold">Total Potongan</td>
          <td></td>
          <td className="px-3 py-2 text-right font-semibold text-red-600">({formatRupiah(slip.total_potongan)})</td>
        </tr>
        <tr className="bg-zinc-900 text-white">
          <td className="px-3 py-3 font-bold font-display">TAKE HOME PAY (Gaji Bulanan)</td>
          <td></td>
          <td className="px-3 py-3 text-right font-bold font-display text-base" data-testid="thp-bulanan">{formatRupiah(slip.thp_bulanan)}</td>
        </tr>
      </tbody>
    </table>
    <p className="text-xs text-zinc-500 italic mb-6">Take Home Pay di atas adalah gaji bulanan yang dibayarkan tanggal 1 bulan berikutnya.</p>

    <p className="overline mb-2">B. Informasi Lembur (Dibayar Mingguan)</p>
    <table className="w-full text-sm border-collapse">
      <tbody className="divide-y divide-zinc-100">
        <tr><td className="px-3 py-1.5">Total Durasi Lembur</td><td className="text-right px-3">{slip.lembur.total_overtime_human}</td></tr>
        <tr><td className="px-3 py-1.5">Total Upah Lembur (gross)</td><td className="text-right px-3">{formatRupiah(slip.lembur.total_overtime_wage)}</td></tr>
        <tr><td className="px-3 py-1.5">Potongan Keterlambatan</td><td className="text-right px-3 text-red-600">({formatRupiah(slip.lembur.total_late_deduction)})</td></tr>
        <tr className="border-t border-zinc-300 bg-zinc-50">
          <td className="px-3 py-2 font-bold">Net Lembur</td>
          <td className="text-right px-3 py-2 font-bold" data-testid="net-lembur">{formatRupiah(slip.lembur.net_lembur)}</td>
        </tr>
      </tbody>
    </table>

    {slip.saturdays.length > 0 && (
      <div className="mt-4">
        <p className="text-xs text-zinc-500 mb-2">Jadwal Pembayaran Lembur (Setiap Sabtu):</p>
        <div className="flex flex-wrap gap-2">
          {slip.saturdays.map((s) => (
            <span key={s.date} className={`text-xs px-2 py-1 rounded-sm border ${s.status === "paid" ? "border-green-600 text-green-700 bg-green-50" : "border-amber-500 text-amber-700 bg-amber-50"}`}>
              {dispDate(s.date)} · {s.status === "paid" ? "SUDAH DIBAYAR" : "AKAN DIBAYAR"}
            </span>
          ))}
        </div>
      </div>
    )}

    <p className="text-xs text-zinc-500 italic mt-4">
      Lembur dibayarkan langsung setiap hari Sabtu untuk akumulasi minggu berjalan.
      Nominal Net Lembur di atas adalah total seluruh bulan; pembayaran aktual dilakukan terbagi per minggu.
    </p>
  </Card>
);
